<template>
  <div>
    <server-card
      v-for="server in filteredServersList"
      :key="server.uuid"
      :server="server"
      :tariff-plan="currentTariffPlan"
      :is-sale="isSale"
    ></server-card>
  </div>
</template>

<script>

import ServerCard from '~/components/common/dedicated-servers/ServerCard'
import addAdditionalData from '~/mixins/servers'

import {mapActions, mapGetters} from 'vuex'
import {State, TariffLine} from '~/rest-client/servers/all/enum'
import {Period} from '~/helpers/enum/period'

export default {
  components: {
    ServerCard
  },
  mixins: [
    addAdditionalData
  ],
  props: {
    isSale: {
      type: Boolean,
      default: false
    },
    isChipcore: {
      type: Boolean,
      default: false
    },
    isInTabs: {
      type: Boolean,
      default: false
    }
  },
  async fetch() {
    await this.fetchPlan()
    await this.fetchAllServersPrices()

    this.tariffPlan = this.getPlan
    this.servers = this.addAdditionalData(this.getAllServersPrices)
  },
  fetchOnServer: true,
  data() {
    return {
      servers: [],
      tariffPlan: [],
      selectedTariffPlan: 'default',
      currentPeriod: Period.MONTH
    }
  },
  fetchKey: 'dedicated-servers',
  computed: {
    ...mapGetters('rest-client/servers/plan', ['getPlan']),
    ...mapGetters('rest-client/servers/all', ['getAllServersPrices']),
    currentTariffPlanList() {
      return this.tariffPlan.filter(plan => plan.type !== 'free')
    },
    currentTariffPlan() {
      return this.currentTariffPlanList.find(plan => {
        if (this.selectedTariffPlan === 'default') {
          return plan.name === '1 month'
        }

        return plan.name === this.selectedTariffPlan
      })
    },
    currentTariffPlanText() {
      const plan = this.currentTariffPlan

      if (plan) {
        if (plan.type === 'day') {
          return this.translations.dayAmount[this.$i18n.locale]({count: plan.period})
        } else if (plan.type === 'month' && plan.discount) {
          return `${this.translations.monthAmount[this.$i18n.locale]({count: plan.period})} -  ${this.$t('discount')} ${plan.discount}%`
        } else {
          return this.translations.monthAmount[this.$i18n.locale]({count: plan.period})
        }
      }

      return plan
    },
    currentTariffPlanPeriod() {
      return this.selectedTariffPlan === '1 day'
        ? Period.DAY
        : Period.MONTH
    },
    serversIsAvailable() {
      return this.servers.filter(server => server.isAvailable)
    },
    filteredServersList() {
      if ((this.isSale || this.isOnlyWithDiscount) && !this.isChipcore) {
        return [
          ...this.entryServers,
          ...this.businessServers,
          ...this.amdServers,
          ...this.performanceServers,
          ...this.graphicServers,
          ...this.chipcoreServers,
          ...this.discountServers
        ].filter(server => this.checkIsSale(server))
      }

      if (this.isChipcore) {
        return this.chipcoreServers.filter(server => this.checkIsSale(server))
      }

      return [
        ...this.entryServers,
        ...this.businessServers,
        ...this.amdServers,
        ...this.performanceServers,
        ...this.graphicServers,
        ...this.chipcoreServers,
        ...this.discountServers
      ]
    },
    entryServers() {
      return this.serversIsAvailable.filter(server => server.tariffLine === TariffLine.ENTRY)
        .sort(this.sortByPrice)
    },
    businessServers() {
      return this.serversIsAvailable.filter(server => server.tariffLine === TariffLine.BUSINESS)
        .sort(this.sortByPrice)
    },
    amdServers() {
      return this.serversIsAvailable.filter(server => server.tariffLine === TariffLine.AMD)
        .sort(this.sortByPrice)
    },
    performanceServers() {
      return this.serversIsAvailable.filter(server => server.tariffLine === TariffLine.PERFORMANCE)
        .sort(this.sortByPrice)
    },
    graphicServers() {
      return this.serversIsAvailable.filter(server => server.tariffLine === TariffLine.GRAPHIC)
        .sort(this.sortByPrice)
    },
    discountServers() {
      return this.serversIsAvailable.filter(server => server.tariffLine === TariffLine.DISCOUNT)
        .sort(this.sortByPrice)
    },
    chipcoreServers() {
      return this.serversIsAvailable.filter(server => server.tariffLine === TariffLine.CHIPCORE)
        .sort(this.sortByPrice)
    }
  },
  async mounted() {
    await this.fetchServices()
  },
  methods: {
    ...mapActions('rest-client/servers/plan', ['fetchPlan']),
    ...mapActions('rest-client/servers/all', ['fetchAllServersPrices']),
    ...mapActions('rest-client/servers/services', ['fetchServices']),
    checkIsSale(server) {
      return this.isSale || this.isOnlyWithDiscount
        ? server.state === State.DISCOUNT && this.getAvailable(server.available)
        : true
    }
  }
}
</script>